import React from "react";
import { ChildrenProps, DefaultContextValue, useRequiredContext } from "@hex-insights/core";
import { PageInfo, PageInfoTitle, RouteParams } from "@hex-insights/router";

// TODO replace HubDetailPagesContext with this

// Context

type HubDetailPageTo = (id: string) => string;

export type HubDetailPageInfo<TitleType extends PageInfoTitle, Params extends RouteParams> = PageInfo<
	TitleType,
	HubDetailPageTo,
	Params
>;

export type HubPagesContextValue = {
	pages: Record<
		string,
		{
			create?: PageInfo<PageInfoTitle, string, RouteParams>;
			detail?: HubDetailPageInfo<PageInfoTitle, RouteParams>;
			index?: PageInfo<PageInfoTitle, string, RouteParams>;
		}
	>;
};

export const HubPagesContext = React.createContext<HubPagesContextValue>(
	new DefaultContextValue("HubPagesContext") as any,
);

// Manager

export type HubPagesManagerProps = {
	/** An object containing PageInfo objects for hub pages in the app. */
	pages: HubPagesContextValue["pages"];
} & ChildrenProps;

/**
 * Holds PageInfo objects for hub pages in an app, not just the active ones.
 */
export function HubPagesManager({ pages, children }: HubPagesManagerProps) {
	const contextValue = React.useMemo(() => ({ pages }), [pages]);

	return <HubPagesContext.Provider value={contextValue}>{children}</HubPagesContext.Provider>;
}

// Hooks

export function useHubPageInfo(key: string, page: "detail"): HubDetailPageInfo<PageInfoTitle, RouteParams>;
export function useHubPageInfo(key: string, page: "create" | "index"): PageInfo<PageInfoTitle, string, RouteParams>;
export function useHubPageInfo(key: string, page: "create" | "detail" | "index") {
	const { pages } = useRequiredContext(HubPagesContext, "useHubPageInfo");
	if (page === "detail") {
		return pages[key]?.detail;
	}
	return pages[key]?.[page];
}
